import { useEffect, useMemo, useState } from 'react'

import { ChevronDown, ChevronRight } from 'lucide-react'

import { ViewSection, ViewSectionContent, ViewSectionHeader } from '@fortem-monorepo/react-spa'
import {
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Separator,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@fortem-monorepo/react-ui'
import { ICG2Guidelines, ICGG2Report } from '@fortem-monorepo/web-sdk'
import { ContentBlocks } from '@fortem-monorepo/web-sdk'

import { GuidelineRow } from '@/pages/funds/ic-guidelines-v2/components/stages/breaches-stage/GuidelineRow'
import {
  emptyTotals,
  getTableLabel,
} from '@/pages/funds/ic-guidelines-v2/components/stages/breaches-stage/breaches-utils'

export interface BreachesStageProps {
  output: ICGG2Report.BreachesStageOutput
  stageOutputs?: ICGG2Report.IcGuidelinesStageOutputs
}

type EvaluationFilter = 'all' | ICG2Guidelines.GuidelineStatus

const DEFAULT_FILTER: EvaluationFilter = 'all'

const findSourceBlock = (
  stageOutputs: ICGG2Report.IcGuidelinesStageOutputs | undefined,
  blockId: string,
): ContentBlocks.DataGridContentBlock | null => {
  if (!stageOutputs) return null

  for (const output of Object.values(stageOutputs)) {
    if (!output || !('blocks' in output)) continue

    const block = output.blocks.find((candidate) => candidate.id === blockId)

    if (block) return block
  }

  return null
}

export const BreachesStage: React.FC<BreachesStageProps> = ({ output, stageOutputs }) => {
  const totals = output.totals ?? emptyTotals()
  const [open, setOpen] = useState(false)
  const [visibleResult, setVisibleResult] = useState<EvaluationFilter>(DEFAULT_FILTER)

  useEffect(() => {
    if (totals.hard > 0 || totals.soft > 0 || totals.failed > 0) {
      setOpen(true)
    }
  }, [totals.failed, totals.hard, totals.soft])

  const filteredGuidelines = useMemo(() => {
    const guidelines = output.guidelines ?? []

    if (visibleResult === 'all') {
      return guidelines
    }

    return guidelines.filter((result) => result.status === visibleResult)
  }, [output.guidelines, visibleResult])

  const groupedGuidelines = useMemo(() => {
    return filteredGuidelines.reduce<Record<string, ICGG2Report.GuidelineResult[]>>(
      (groups, guideline) => {
        const table = guideline.guideline.table

        groups[table] ??= []
        groups[table].push(guideline)

        return groups
      },
      {},
    )
  }, [filteredGuidelines])

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <ViewSection>
        <ViewSectionHeader className={'mb-3'}>
          <CollapsibleTrigger asChild>
            <button
              type={'button'}
              className={
                'flex w-full items-center gap-3 border-b-4 border-b-gray-200 pb-3 text-left'
              }
            >
              {open ? <ChevronDown className={'size-4'} /> : <ChevronRight className={'size-4'} />}
              <span className={'text-xl font-semibold tracking-wide text-heading dark:text-white'}>
                Guideline Evaluations
              </span>
              <div className={'flex flex-wrap gap-2'}>
                <Badge theme={'success'} variant={'outline'}>
                  Ok: {totals.ok}
                </Badge>
                {totals.hard > 0 && (
                  <Badge theme={totals.hard > 0 ? 'destructive' : undefined} variant={'outline'}>
                    Hard: {totals.hard}
                  </Badge>
                )}
                {totals.soft > 0 && (
                  <Badge theme={totals.soft > 0 ? 'warning' : undefined} variant={'outline'}>
                    Soft: {totals.soft}
                  </Badge>
                )}
                {totals.failed > 0 && (
                  <Badge theme={totals.failed > 0 ? 'info' : undefined} variant={'outline'}>
                    Issues: {totals.failed}
                  </Badge>
                )}
              </div>
            </button>
          </CollapsibleTrigger>
        </ViewSectionHeader>

        <CollapsibleContent>
          <ViewSectionContent className={'space-y-4'}>
            {output.guidelines.length === 0 && (
              <p className={'rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm'}>
                No guidelines found to evaluate.
              </p>
            )}

            {output.guidelines.length > 0 && (
              <>
                <div className={'mt-4'}>
                  <span className={'text-muted-foreground tracking-wider text-sm'}>
                    COMPLIANCE OVERVIEW
                  </span>
                  <div className={'flex items-center gap-px mt-2'}>
                    {totals.hard > 0 && (
                      <div
                        className={'bg-red-400 rounded-full h-2'}
                        style={{ width: `${(totals.hard / output.guidelines.length) * 100}%` }}
                      />
                    )}
                    {totals.soft > 0 && (
                      <div
                        className={'bg-amber-300 rounded-full h-2'}
                        style={{ width: `${(totals.soft / output.guidelines.length) * 100}%` }}
                      />
                    )}
                    {totals.ok > 0 && (
                      <div
                        className={'bg-emerald-400 rounded-full h-2'}
                        style={{ width: `${(totals.ok / output.guidelines.length) * 100}%` }}
                      />
                    )}
                    {totals.failed > 0 && (
                      <div
                        className={'bg-blue-400 rounded-full h-2'}
                        style={{ width: `${(totals.failed / output.guidelines.length) * 100}%` }}
                      />
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    {totals.hard > 0 && (
                      <div className={'inline-flex items-center gap-1'}>
                        <span className={'size-2 rounded-full bg-red-400'} />
                        <span className={'text-muted-foreground tracking-wide text-xs'}>
                          Hard breach
                        </span>
                      </div>
                    )}
                    {totals.soft > 0 && (
                      <div className={'inline-flex items-center gap-1'}>
                        <span className={'size-2 rounded-full bg-amber-300'} />
                        <span className={'text-muted-foreground tracking-wide text-xs'}>
                          Soft breach
                        </span>
                      </div>
                    )}
                    {totals.ok > 0 && (
                      <div className={'inline-flex items-center gap-1'}>
                        <span className={'size-2 rounded-full bg-emerald-400'} />
                        <span className={'text-muted-foreground tracking-wide text-xs'}>Pass</span>
                      </div>
                    )}
                    {totals.failed > 0 && (
                      <div className={'inline-flex items-center gap-1'}>
                        <span className={'size-2 rounded-full bg-blue-400'} />
                        <span className={'text-muted-foreground tracking-wide text-xs'}>
                          Failed
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                <Tabs
                  value={visibleResult}
                  onValueChange={(value) => setVisibleResult(value as EvaluationFilter)}
                  className={'my-4'}
                >
                  <TabsList className={'flex-wrap'}>
                    <TabsTrigger value={'all'} className="px-4">
                      All
                      <span className={'text-muted-foreground'}>
                        {Object.values(totals).reduce((prev, value) => prev + value, 0)}
                      </span>
                    </TabsTrigger>
                    {totals.ok > 0 && (
                      <TabsTrigger value={'ok'} className="px-4">
                        Ok
                        <span className={'text-muted-foreground'}>{totals.ok}</span>
                      </TabsTrigger>
                    )}
                    {totals.hard > 0 && (
                      <TabsTrigger value={'hard'} className="px-4">
                        Hard
                        <span className={'text-muted-foreground'}>{totals.hard}</span>
                      </TabsTrigger>
                    )}
                    {totals.soft > 0 && (
                      <TabsTrigger value={'soft'} className="px-4">
                        Soft
                        <span className={'text-muted-foreground'}>{totals.soft}</span>
                      </TabsTrigger>
                    )}
                    {totals.failed > 0 && (
                      <TabsTrigger value={'failed'} className="px-4">
                        Issues
                        <span className={'text-muted-foreground'}>{totals.failed}</span>
                      </TabsTrigger>
                    )}
                  </TabsList>
                </Tabs>

                <div className={'space-y-5'}>
                  {Object.entries(groupedGuidelines).map(([table, guidelines]) => (
                    <div key={table} className={'space-y-2'}>
                      <div
                        className={
                          'flex items-center justify-between border-b border-gray-200 pb-2'
                        }
                      >
                        <span className={'text-sm font-semibold text-[#333333]'}>
                          {getTableLabel(table)}
                        </span>
                        <span className={'text-xs text-muted-foreground'}>
                          {guidelines.length} guideline{guidelines.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      {guidelines.map((guideline) => (
                        <GuidelineRow
                          item={guideline}
                          sourceBlock={findSourceBlock(stageOutputs, guideline.guideline.table)}
                          key={guideline.guideline.id ?? `${table}-${guideline.guideline.name}`}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </>
            )}
          </ViewSectionContent>
        </CollapsibleContent>
      </ViewSection>
    </Collapsible>
  )
}
